import * as React from "react"
import { useIntl } from "react-intl"
import { Input } from "@/components/ui/input"
import { Radio, RadioGroup } from "@/components/ui/radio"
import { useFormat } from "@/lib/format"
import type { ExamVersionOption } from "@/types/exam"

export type ExamSettingsData = {
  target_exam_date: string
  exam_version_id: string
}

/**
 * Target exam date + exam version fields. Picking a date preselects the exam
 * version given on that date, until the user chooses a version themselves.
 */
export function ExamSettingsFields({
  versions,
  data,
  setData,
  errors,
}: {
  versions: ExamVersionOption[]
  data: ExamSettingsData
  setData: (key: keyof ExamSettingsData, value: string) => void
  errors: Partial<Record<string, string>>
}) {
  const intl = useIntl()
  const format = useFormat()
  const [versionTouched, setVersionTouched] = React.useState(false)

  const changeDate = (date: string) => {
    setData("target_exam_date", date)
    if (versionTouched || !date) return
    const match = versions.find((v) => covers(v, date))
    if (match) setData("exam_version_id", String(match.id))
  }

  function dateRange(version: ExamVersionOption) {
    const { starts_on: start, ends_on: end } = version
    if (start && !end) return intl.formatMessage({ id: "exam_fields.from" }, { date: format.date(start) })
    if (end && !start) return intl.formatMessage({ id: "exam_fields.through" }, { date: format.date(end) })
    if (start && end) return intl.formatMessage({ id: "exam_fields.between" }, { start: format.date(start), end: format.date(end) })
    return ""
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <label htmlFor="target_exam_date">{intl.formatMessage({ id: "exam_fields.target_date" })}</label>
        <Input
          id="target_exam_date"
          type="date"
          aria-invalid={!!errors.target_exam_date}
          aria-describedby="target_exam_date_help"
          value={data.target_exam_date}
          onChange={(e) => changeDate(e.target.value)}
        />
        <p id="target_exam_date_help" className="text-xs text-ink-muted">
          {intl.formatMessage({ id: "exam_fields.target_date_hint" })}
        </p>
        {errors.target_exam_date && (
          <p className="text-xs text-danger-display">{errors.target_exam_date}</p>
        )}
      </div>

      <fieldset className="space-y-3">
        <legend>{intl.formatMessage({ id: "exam_fields.version" })}</legend>
        <RadioGroup>
          {versions.map((version) => (
            <label
              key={version.id}
              className="flex cursor-pointer items-start gap-3 font-normal text-ink-body"
            >
              <Radio
                name="exam_version_id"
                value={version.id}
                className="mt-1"
                checked={data.exam_version_id === String(version.id)}
                onChange={() => {
                  setVersionTouched(true)
                  setData("exam_version_id", String(version.id))
                }}
              />
              <span>
                <span className="block text-ink-display">{version.name}</span>
                <span className="block text-xs text-ink-muted">{dateRange(version)}</span>
              </span>
            </label>
          ))}
        </RadioGroup>
        {errors.exam_version && (
          <p className="text-xs text-danger-display">{errors.exam_version}</p>
        )}
      </fieldset>
    </div>
  )
}

function covers(version: ExamVersionOption, date: string) {
  return (!version.starts_on || version.starts_on <= date) && (!version.ends_on || version.ends_on >= date)
}
